import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { useAuth } from '../api';
import { authApi } from '../api';
import {
  User,
  Mail,
  Phone,
  Lock,
  Save,
  ArrowLeft,
  Info,
  Eye,
  EyeOff,
  ShieldCheck,
} from 'lucide-react';

export default function MyProfile() {
  const { user, setUser } = useAuth();
  const navigate = useNavigate();

  const [profileForm, setProfileForm] = useState({
    full_name: user?.full_name || '',
    email: user?.email || '',
    phone: user?.phone || '',
  });

  const [passwordForm, setPasswordForm] = useState({
    current_password: '',
    new_password: '',
    confirm_password: '',
  });

  useEffect(() => {
    let cancelled = false;
    authApi.me()
      .then(res => {
        if (cancelled) return;
        const fresh = res.data;
        setProfileForm({
          full_name: fresh.full_name,
          email: fresh.email,
          phone: fresh.phone || '',
        });
      })
      .catch(() => toast.error('Erro ao carregar perfil. Sessão pode ter expirado.'));
    return () => { cancelled = true; };
  }, []);

  const [showCurrent, setShowCurrent] = useState(false);
  const [showNew, setShowNew] = useState(false);
  const [showConfirm, setShowConfirm] = useState(false);
  const [savingProfile, setSavingProfile] = useState(false);
  const [savingPassword, setSavingPassword] = useState(false);

  const handleProfileChange = (e) => {
    setProfileForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handlePasswordChange = (e) => {
    setPasswordForm(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setSavingProfile(true);
    try {
      const res = await authApi.updateMe({
        full_name: profileForm.full_name,
        email: profileForm.email,
        phone: profileForm.phone || null,
      });
      const updated = res.data;
      const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
      const newUser = {
        ...storedUser,
        full_name: updated.full_name,
        email: updated.email,
        phone: updated.phone,
      };
      localStorage.setItem('user', JSON.stringify(newUser));
      if (typeof setUser === 'function') setUser(newUser);
      toast.success('Dados atualizados com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao salvar dados.');
    } finally {
      setSavingProfile(false);
    }
  };

  const handleSavePassword = async (e) => {
    e.preventDefault();
    if (passwordForm.new_password !== passwordForm.confirm_password) {
      toast.error('A nova senha e a confirmação não coincidem.');
      return;
    }
    setSavingPassword(true);
    try {
      await authApi.updateMe({
        current_password: passwordForm.current_password,
        new_password: passwordForm.new_password,
      });
      setPasswordForm({ current_password: '', new_password: '', confirm_password: '' });
      toast.success('Senha alterada com sucesso!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erro ao alterar senha.');
    } finally {
      setSavingPassword(false);
    }
  };

  const initials = (user?.full_name || 'U')
    .split(' ')
    .slice(0, 2)
    .map(w => w[0])
    .join('')
    .toUpperCase();

  return (
    <div className="max-w-2xl mx-auto space-y-6">

      {/* Header */}
      <div className="flex items-center gap-4">
        <button
          onClick={() => navigate(-1)}
          className="p-2 text-gray-400 hover:text-white hover:bg-gray-700 rounded-lg transition-colors"
        >
          <ArrowLeft size={20} />
        </button>
        <div>
          <h1 className="text-2xl font-bold text-slate-900">Meu Perfil</h1>
          <p className="text-sm text-gray-400">Gerencie suas informações pessoais e senha de acesso</p>
        </div>
      </div>

      {/* Avatar + info summary */}
      <div className="card p-6 flex items-center gap-5">
        <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center text-white text-xl font-bold flex-shrink-0">
          {initials}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-lg font-semibold text-white truncate">{user?.full_name}</p>
          <p className="text-sm text-gray-400">{user?.position || '—'}</p>
          <p className="text-sm text-gray-400">{user?.department || '—'}</p>
          {user?.is_admin && (
            <span className="inline-flex items-center gap-1 mt-1 text-xs text-green-400 bg-green-500/10 border border-green-500/30 rounded-full px-2 py-0.5">
              <ShieldCheck size={12} /> Administrador
            </span>
          )}
        </div>
        {user?.sector_name && (
          <div className="hidden sm:block text-right">
            <p className="text-xs text-gray-500">Setor</p>
            <p className="text-sm text-white font-medium">{user.sector_name}</p>
          </div>
        )}
      </div>

      {/* Dados pessoais */}
      <div className="card p-6">
        <h2 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
          <User size={16} className="text-green-400" /> Dados Pessoais
        </h2>
        <p className="text-xs text-gray-400 mb-4">
          Você pode editar seu nome, e-mail e telefone. Cargo e departamento só podem ser alterados pelo administrador.
        </p>

        <form onSubmit={handleSaveProfile} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Nome completo</label>
            <input
              name="full_name"
              value={profileForm.full_name}
              onChange={handleProfileChange}
              className="input w-full"
              required
              minLength={2}
            />
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">E-mail</label>
            <div className="relative">
              <Mail size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                name="email"
                type="email"
                value={profileForm.email}
                onChange={handleProfileChange}
                className="input w-full pl-9"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">Telefone</label>
            <div className="relative">
              <Phone size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input
                name="phone"
                value={profileForm.phone}
                onChange={handleProfileChange}
                className="input w-full pl-9"
                placeholder="(00) 00000-0000"
              />
            </div>
          </div>

          {/* Read-only hint fields */}
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm text-gray-300 mb-1 flex items-center gap-1">
                Cargo
                <span title="Somente o administrador pode alterar">
                  <Info size={12} className="text-gray-500" />
                </span>
              </label>
              <input
                value={user?.position || '—'}
                className="input w-full opacity-50 cursor-not-allowed"
                readOnly
                tabIndex={-1}
              />
            </div>
            <div>
              <label className="block text-sm text-gray-300 mb-1 flex items-center gap-1">
                Departamento
                <span title="Somente o administrador pode alterar">
                  <Info size={12} className="text-gray-500" />
                </span>
              </label>
              <input
                value={user?.department || '—'}
                className="input w-full opacity-50 cursor-not-allowed"
                readOnly
                tabIndex={-1}
              />
            </div>
          </div>

          <div className="flex justify-end pt-2">
            <button type="submit" disabled={savingProfile} className="btn-primary flex items-center gap-2">
              <Save size={16} />
              {savingProfile ? 'Salvando...' : 'Salvar dados'}
            </button>
          </div>
        </form>
      </div>

      {/* Alterar senha */}
      <div className="card p-6">
        <h2 className="text-base font-semibold text-white mb-1 flex items-center gap-2">
          <Lock size={16} className="text-green-400" /> Alterar Senha
        </h2>
        <p className="text-xs text-gray-400 mb-4">
          Para alterar sua senha, confirme a senha atual e informe a nova senha (mínimo 6 caracteres).
        </p>

        <form onSubmit={handleSavePassword} className="space-y-4">
          <div>
            <label className="block text-sm text-gray-300 mb-1">Senha atual</label>
            <div className="relative">
              <input
                name="current_password"
                type={showCurrent ? 'text' : 'password'}
                value={passwordForm.current_password}
                onChange={handlePasswordChange}
                className="input w-full pr-10"
                required
                autoComplete="current-password"
              />
              <button
                type="button"
                onClick={() => setShowCurrent(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showCurrent ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">Nova senha</label>
            <div className="relative">
              <input
                name="new_password"
                type={showNew ? 'text' : 'password'}
                value={passwordForm.new_password}
                onChange={handlePasswordChange}
                className="input w-full pr-10"
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowNew(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showNew ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm text-gray-300 mb-1">Confirmar nova senha</label>
            <div className="relative">
              <input
                name="confirm_password"
                type={showConfirm ? 'text' : 'password'}
                value={passwordForm.confirm_password}
                onChange={handlePasswordChange}
                className="input w-full pr-10"
                required
                minLength={6}
                autoComplete="new-password"
              />
              <button
                type="button"
                onClick={() => setShowConfirm(v => !v)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-white"
              >
                {showConfirm ? <EyeOff size={16} /> : <Eye size={16} />}
              </button>
            </div>
            {passwordForm.confirm_password &&
              passwordForm.new_password !== passwordForm.confirm_password && (
                <p className="text-xs text-red-400 mt-1">As senhas não coincidem.</p>
              )}
          </div>

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={savingPassword || passwordForm.new_password !== passwordForm.confirm_password}
              className="btn-primary flex items-center gap-2 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Lock size={16} />
              {savingPassword ? 'Alterando...' : 'Alterar senha'}
            </button>
          </div>
        </form>
      </div>

    </div>
  );
}
